import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setTokenHolder } from "@/lib/api";

// Der Token liegt im localStorage und nicht im Speicher der Seite: sonst muss
// man sich nach jedem Neuladen neu anmelden, und die App wird täglich benutzt.
// Das ist eine bewusste Abwägung — ein XSS könnte ihn lesen. Gegen die
// Alternative (httpOnly-Cookie) spricht, dass Frontend und Backend hier unter
// verschiedenen Ports laufen und dann CSRF-Schutz dazukäme; das ist mehr
// Bewegliches als der Gewinn hergibt.
const SCHLUESSEL = "mieteronline.token";

export const tokenLesen = () => {
    try { return localStorage.getItem(SCHLUESSEL) || null; } catch { return null; }
};
const tokenSchreiben = (t) => {
    try { t ? localStorage.setItem(SCHLUESSEL, t) : localStorage.removeItem(SCHLUESSEL); } catch { /* Speicher gesperrt */ }
};

// api.js kennt den Token nicht selbst, sondern fragt hier nach. So bleibt der
// Speicherort an einer Stelle und der Interceptor muss nichts importieren, was
// wiederum ihn importiert.
setTokenHolder(tokenLesen);

// Vier Rollen, wie im Backend und im Aufteiler. `admin` ist seit dem
// 09.09.2026 die höchste eigene Rolle und nicht mehr ein Altwert für
// „verwalten" — wer sie hat, sieht den ganzen Bestand, unabhängig von seiner
// Bereichszuordnung.
const RANG = { lesen: 1, schreiben: 2, verwalten: 3, admin: 4 };

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [nutzer, setNutzer] = useState(null);
    const [laedt, setLaedt] = useState(true);

    // Beim Start den Token gegen /auth/me prüfen statt ihm zu glauben: er kann
    // abgelaufen sein, der Zugang stillgelegt oder die Rolle geändert.
    useEffect(() => {
        let abgebrochen = false;
        const pruefen = async () => {
            if (!tokenLesen()) { setLaedt(false); return; }
            try {
                const { data } = await api.get("/auth/me");
                if (!abgebrochen) setNutzer(data);
            } catch {
                tokenSchreiben(null);
            } finally {
                if (!abgebrochen) setLaedt(false);
            }
        };
        pruefen();
        return () => { abgebrochen = true; };
    }, []);

    // Ein 401 aus irgendeiner Anfrage heißt: die Sitzung ist vorbei. Zentral
    // abmelden, statt jede Seite ihren eigenen Umgang damit erfinden zu lassen.
    useEffect(() => {
        const bei401 = () => { tokenSchreiben(null); setNutzer(null); };
        window.addEventListener("auth:abgelaufen", bei401);
        return () => window.removeEventListener("auth:abgelaufen", bei401);
    }, []);

    const anmelden = useCallback(async (email, passwort) => {
        const { data } = await api.post("/auth/login", { email, password: passwort });
        tokenSchreiben(data.access_token);
        setNutzer(data.user);
        return data.user;
    }, []);

    const abmelden = useCallback(() => {
        tokenSchreiben(null);
        setNutzer(null);
    }, []);

    const wert = useMemo(() => {
        const rolle = nutzer?.role || null;
        const rang = RANG[rolle] || 0;
        return {
            nutzer, laedt, anmelden, abmelden, setNutzer,
            rolle,
            angemeldet: !!nutzer,
            darfSchreiben: rang >= RANG.schreiben,
            darfVerwalten: rang >= RANG.verwalten,
            istAdmin: rang >= RANG.admin,
            // Nur ein Admin oder ein ausdrücklich unbeschränkter Zugang sieht
            // alles. Die Oberfläche braucht das, um Knöpfe auszublenden, die
            // ohnehin mit 403 zurückkämen.
            unbeschraenkt: rang >= RANG.admin || !!nutzer?.alle_bereiche,
            nurLesen: !!nutzer && rang < RANG.schreiben,
        };
    }, [nutzer, laedt, anmelden, abmelden]);

    return <AuthContext.Provider value={wert}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth außerhalb von AuthProvider benutzt");
    return ctx;
};

/**
 * Für schreibende Bedienelemente: `disabled={!darfSchreiben}` plus ein Grund.
 * Absichtlich ein Haken und keine Komponente — die Knöpfe dieser App sind
 * shadcn-Buttons mit sehr verschiedenen Aufrufen, ein Wrapper würde die
 * Aufrufstellen umschreiben statt sie zu ergänzen.
 */
export const useSchreibschutz = () => {
    const { darfSchreiben, rolle } = useAuth();
    return {
        gesperrt: !darfSchreiben,
        // Auf den Knopf als title: die Auskunft muss dort stehen, wo geklickt
        // wird, nicht erst in der Fehlermeldung danach.
        grund: darfSchreiben ? undefined
            : `Dein Zugang hat die Rolle "${rolle}" und darf nur lesen.`,
    };
};
